import React from "react";
import { Link } from "react-router";

/** The Kindred Live coupons search, prefiltered; the page reads these on load. */
export const liveCouponSearch = (
  code: string,
  brandId?: string | null,
  country?: string | null
) => {
  const p = new URLSearchParams({ code });
  if (brandId) p.set("brandId", brandId);
  if (country) p.set("country", country);
  return `/kindred/coupons?${p.toString()}`;
};

/**
 * Prefer a shopper country the coupon is enabled in: the live search filters
 * on enabled countries, so any other country would come back empty.
 */
export const searchCountry = (
  shopper: string[],
  markets: string[]
): string | null =>
  shopper.find((c) => markets.includes(c)) ?? markets[0] ?? shopper[0] ?? null;

const IconLink: React.FC<{ to: string; icon: string; title: string }> = ({
  to,
  icon,
  title,
}) => (
  <Link
    to={to}
    target="_blank"
    rel="noreferrer"
    title={title}
    onClick={(e) => e.stopPropagation()}
  >
    <i className={`bi ${icon}`} />
  </Link>
);

/** Next to a code: the Kindred Live coupons search for it. */
export const CouponSearchLink: React.FC<{
  code: string | null;
  brandId: string | null;
  country: string | null;
}> = ({ code, brandId, country }) =>
  code ? (
    <span className="ms-2 small">
      <IconLink
        to={liveCouponSearch(code, brandId, country)}
        icon="bi-search"
        title={`Find ${code} in Kindred Live coupons${
          brandId ? " for this brand" : ""
        }${country ? ` in ${country}` : ""}`}
      />
    </span>
  ) : null;

/** Next to a brand name: the Kindred brand page. */
export const BrandLink: React.FC<{ brandId: string | null }> = ({ brandId }) =>
  brandId ? (
    <span className="ms-2 small">
      <IconLink
        to={`/kindred/brands/${brandId}`}
        icon="bi-shop"
        title={`Open the brand (${brandId})`}
      />
    </span>
  ) : null;

/** The deal id cut to four characters, linking to the deal; hover for the full id. */
export const DealLink: React.FC<{ campaignId: string | null }> = ({
  campaignId,
}) =>
  campaignId ? (
    <Link
      to={`/kindred/deals/${campaignId}`}
      target="_blank"
      rel="noreferrer"
      className="font-monospace"
      title={`Open the deal this code ran under (${campaignId})`}
      onClick={(e) => e.stopPropagation()}
    >
      {campaignId.slice(0, 4)}
    </Link>
  ) : (
    <span className="text-muted">—</span>
  );
